import { useState } from "react";
import { Play } from "lucide-react";

const VIDEO_ID = "pmtDG4j1LgA";
const VIDEO_TITLE = "AntAtlas — Catch Confluence docs that lie about your code (2 min walkthrough)";

const FreshnessShowcase = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="hidden section-padding" id="freshness-preview">
      <div className="container-wide">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-medium uppercase tracking-widest text-accent mb-3">
            See it work
          </p>
          <h2 className="heading-lg mb-4">
            2 minutes on the actual product.
          </h2>
          <p className="body-md text-muted-foreground">
            Founder walkthrough of stale-doc detection, commit attribution, and approve-to-publish on
            real GitHub + Confluence data. Click to play.
          </p>
        </div>

        <figure className="relative max-w-5xl mx-auto animate-fade-in-up">
          <div className="relative rounded-xl overflow-hidden border border-border/60 bg-surface-elevated neon-glow-accent">
            {/* Browser-chrome top bar — matches the UI screenshot framing pattern */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border/60 bg-surface-subtle">
              <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
              <span className="h-2.5 w-2.5 rounded-full bg-warning/60" />
              <span className="h-2.5 w-2.5 rounded-full bg-success/60" />
            </div>

            {/* Facade pattern: show poster image until user clicks play. Saves
                ~500 KB of YouTube SDK on initial page load + avoids tracking
                cookies until interaction (youtube-nocookie + click-gate). */}
            <div className="relative aspect-video bg-black">
              {playing ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                  title={VIDEO_TITLE}
                  loading="lazy"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 w-full h-full group block bg-cover bg-center"
                  style={{
                    backgroundImage: `url(https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg)`,
                  }}
                  aria-label={`Play: ${VIDEO_TITLE}`}
                >
                  <span className="absolute inset-0 bg-black/30 group-hover:bg-black/15 transition-colors" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-[0_0_40px_hsl(var(--neon-accent)/0.5)] group-hover:scale-110 group-hover:shadow-[0_0_60px_hsl(var(--neon-accent)/0.7)] transition-all duration-300">
                      <Play className="w-9 h-9 md:w-10 md:h-10 ml-1.5" fill="currentColor" />
                    </span>
                  </span>
                  <span className="absolute bottom-4 right-4 px-3 py-1.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-xs font-semibold tracking-wide">
                    2:00 · founder walkthrough
                  </span>
                </button>
              )}
            </div>
          </div>
        </figure>
      </div>

      {/* Schema.org VideoObject for AI/SERP citations. ChatGPT/Perplexity
          preferentially cite pages with VideoObject markup when buyers ask
          "show me a demo of [tool]". */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: VIDEO_TITLE,
            description:
              "Founder walkthrough of the first AntAtlas pilot version. Shows stale-doc detection with commit attribution, AI-suggested diff, human review queue, coverage gaps, and the full sync loop on real GitHub + Confluence data.",
            thumbnailUrl: `https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`,
            uploadDate: "2026-06-01",
            duration: "PT2M",
            embedUrl: `https://www.youtube-nocookie.com/embed/${VIDEO_ID}`,
            contentUrl: `https://www.youtube.com/watch?v=${VIDEO_ID}`,
            publisher: {
              "@type": "Organization",
              name: "AntAtlas",
              url: "https://antatlas.ai",
            },
          }),
        }}
      />
    </section>
  );
};

export default FreshnessShowcase;
